/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { makeObservable, observable, action, flow, computed } from 'mobx';
import { GlobalStore, UserStore } from '@roc/feature-app-core';
import { ExtensionsInternalService } from '../services/extensionsInternalService';
import { LoanStore } from '@roc/feature-loans';
import { format } from 'date-fns';

export class RequestExtensionStore {
  loanStore: LoanStore;
  userStore: UserStore;
  globalStore: GlobalStore;
  private extensionsService: ExtensionsInternalService;
  notes = '';
  loanId = '';
  dealName = '';
  totalLoanAmount = '';
  isSubmitting = false;
  extensionWarnings = '';
  totalExtensionFee = '';
  extensionDuration = '';
  extensionInsurance = '';
  disableNewMaturityDate = true;
  existingMaturityDate = '';
  customNewMaturityDate: Date | null = null;

  constructor(globalStore: GlobalStore, loanStore: LoanStore, userStore: UserStore) {
    this.loanStore = loanStore;
    this.userStore = userStore;
    this.globalStore = globalStore;
    this.loanId = '';
    this.dealName = '';
    this.extensionsService = new ExtensionsInternalService();
    this.existingMaturityDate = '';
    this.totalLoanAmount = '';

    makeObservable(this, {
      reset: action,
      setField: action,
      loanId: observable,
      notes: observable,
      dealName: observable,
      isSubmitting: observable,
      requestDate: computed,
      totalLoanAmount: observable,
      submitLoanExtension: flow,
      newMaturityDate: computed,
      extensionDuration: observable,
      extensionWarnings: observable,
      totalExtensionFee: observable,
      extensionInsurance: observable,
      existingMaturityDate: observable,
      initRequestExtension: flow,
      loadLoanData: action,
      disableNewMaturityDate: observable,
      customNewMaturityDate: observable,
    });
  }

  *initRequestExtension(loanId: string) {
    yield this.loanStore.fetchLoanDetails(loanId);
    this.loadLoanData();
  }

  loadLoanData() {
    if (this.loanStore.loanDetails) {
      this.loanId = this.loanStore.loanDetails.loanId || '';
      this.dealName = this.loanStore.loanDetails.dealName || '';
      this.totalLoanAmount = this.loanStore.loanDetails.amount?.toString() || '';
      this.existingMaturityDate = this.loanStore.loanDetails.extendedMaturityDate || this.loanStore.loanDetails.maturityDate || '';
    }
  }

  get requestDate(): string {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const year = today.getFullYear();
    return `${month}/${day}/${year}`;
  }

  get newMaturityDate(): Date | null {
    if (this.extensionDuration === 'custom') {
      this.disableNewMaturityDate = false;
      return this.customNewMaturityDate;
    }
    this.disableNewMaturityDate = true;
    if (!this.extensionDuration || !this.existingMaturityDate) return null;

    const months = parseInt(this.extensionDuration, 10);
    const newDate = new Date(this.existingMaturityDate);
    newDate.setMonth(newDate.getMonth() + months);
    return newDate;
  }

  *submitLoanExtension() {
    this.isSubmitting = true;
    const newMaturityDateValue = this.newMaturityDate ? format(this.newMaturityDate, 'MM/dd/yyyy') : null;
    // nportal only honors the submitted newMaturityDate when extensionDuration is the
    // -1 "Custom" sentinel; any other value makes it recompute the date as
    // maxMaturityDate + N months, discarding the exact day the user picked.
    const extensionDurationValue =
      this.extensionDuration === 'custom' ? -1 : parseInt(this.extensionDuration, 10);
    try {
      const payload = {
        notes: this.notes,
        loanId: this.loanId,
        dealName: this.dealName,
        requestDate: this.requestDate,
        newMaturityDate: newMaturityDateValue,
        extensionDuration: extensionDurationValue,
        extensionWarnings: this.extensionWarnings,
        totalExtensionFee: this.totalExtensionFee,
        extensionInsurance: this.extensionInsurance,
        existingMaturityDate: this.existingMaturityDate,
      };

      const response = yield this.extensionsService.submitLoanExtension(payload);

      const result = typeof response.data.data === 'string'
        ? JSON.parse(response.data.data)
        : response.data.data;

      if (result.success === false) {
        this.globalStore.notificationStore.showErrorNotification({
          message: result.responseMessage || 'Failed to submit extension request',
        });
        return false;
      }

      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Extension request submitted successfully',
      });

      this.reset();
      return true;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to submit extension request',
      });
      return false;
    } finally {
      this.isSubmitting = false;
    }
  }

  setField(field: string, value: any) {
    // Special handling for customNewMaturityDate
    if (field === 'customNewMaturityDate') {
      this.customNewMaturityDate = value;
      return;
    }
    this[field] = value;
  }

  reset() {
    this.notes = '';
    this.extensionDuration = '';
    this.extensionWarnings = '';
    this.totalExtensionFee = '';
    this.extensionInsurance = '';
  }

  get isValid(): boolean {
    const hasRequiredFields = this.extensionDuration !== '' && this.extensionInsurance !== '' && this.totalExtensionFee !== '';

    if (!hasRequiredFields) return false;

    const totalLoanAmount = parseFloat(this.totalLoanAmount?.replace(/[^0-9.]/g, '') || '0');
    const currentFee = parseFloat(this.totalExtensionFee?.replace(/[^0-9.]/g, '') || '0');
    const maxAllowed = totalLoanAmount * 0.1;

    return currentFee <= maxAllowed;
  }
}
